import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { BulkBar, runBulk, type BulkSelect } from '../lib/bulkSelect';

type TeamOpt = { id: string; name: string };
/** Lo que devuelve /api/departments/:key/statuses: OBJETOS, no strings. */
type StatusOpt = { key: string; label: string };

/**
 * ACCIONES EN LOTE SOBRE CLAIMS — la barra flotante que sale al seleccionar filas.
 *
 * Es la misma en todas las vistas del CRM (To Do, colas de pago, boards de departamento): lo
 * único que cambia es el departamento, que decide a qué equipos se puede asignar y a qué status
 * se puede mover. No usa endpoints nuevos: llama los MISMOS que ya usa cada fila, de a pocos a la
 * vez, y si alguno falla lo dice — en lote siempre hay alguno que falla (permisos, un claim que
 * cambió mientras tanto) y hay que verlo, no suponer que salió todo bien.
 */
export function ClaimBulkBar({ sel, dept, onDone }: {
  sel: BulkSelect;
  /** Departamento de la vista: de ahí salen los equipos y los status a los que se puede mover. */
  dept: string;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [teams, setTeams] = useState<TeamOpt[]>([]);
  const [statuses, setStatuses] = useState<StatusOpt[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (!sel.count) return;
    api.get<{ teams: TeamOpt[] }>(`/api/teams/options?department=${encodeURIComponent(dept)}`)
      .then((r) => setTeams(r.teams || [])).catch(() => setTeams([]));
    api.get<{ statuses: StatusOpt[] }>(`/api/departments/${encodeURIComponent(dept)}/statuses`)
      .then((r) => setStatuses(r.statuses || [])).catch(() => setStatuses([]));
  }, [dept, sel.count]);

  /** Corre `fn` sobre los seleccionados mostrando el avance y el resultado (parcial si lo hubo). */
  const run = async (fn: (id: string) => Promise<unknown>) => {
    const ids = sel.selected;
    if (!ids.length) return;
    setResult(null);
    setBusy(t('bulk.working', { done: '0', total: String(ids.length) }));
    const res = await runBulk(ids, fn, (done, total) =>
      setBusy(t('bulk.working', { done: String(done), total: String(total) })));
    setBusy(null);
    setResult(res.failed.length
      ? t('bulk.donePartial', { ok: String(res.ok), failed: String(res.failed.length) })
      : t('bulk.doneOk', { n: String(res.ok) }));
    sel.clear();
    onDone();
  };

  const assignTeam = (teamId: string) =>
    run((id) => api.post(`/api/claims/${id}/departments/${encodeURIComponent(dept)}/assigned-team`, { teamId }));

  const moveStatus = (status: string) =>
    run((id) => api.post(`/api/claims/${id}/departments/${encodeURIComponent(dept)}/status`, { status }));

  const logFollowUp = () =>
    run((id) => api.post(`/api/claims/${id}/follow-ups`, { stage: dept, channel: 'note', note: t('bulk.fuNote'), department: dept }));

  const archive = () => {
    // Archivar saca el claim de TODAS las vistas: se confirma antes, y con el número exacto.
    if (!window.confirm(t('bulk.archiveConfirm', { n: String(sel.count) }))) return;
    void run((id) => api.post(`/api/claims/${id}/archive`, { archived: true }));
  };

  return (
    <>
      <BulkBar
        count={sel.count}
        clear={sel.clear}
        busy={busy}
        actions={[
          {
            key: 'assign',
            label: t('bulk.assign'),
            render: () => (
              <select className="input sm" style={{ width: 'auto', minWidth: 150 }} defaultValue=""
                onChange={(e) => { const v = e.target.value; e.currentTarget.value = ''; if (v) void assignTeam(v); }}>
                <option value="">{t('bulk.assign')}</option>
                {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
              </select>
            ),
          },
          {
            key: 'status',
            label: t('bulk.moveTo'),
            render: () => (
              <select className="input sm" style={{ width: 'auto', minWidth: 150 }} defaultValue=""
                onChange={(e) => { const v = e.target.value; e.currentTarget.value = ''; if (v) void moveStatus(v); }}>
                <option value="">{t('bulk.moveTo')}</option>
                {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            ),
          },
          { key: 'followup', label: t('bulk.followUp'), onRun: () => { void logFollowUp(); } },
          { key: 'archive', label: t('bulk.archive'), danger: true, onRun: archive },
        ]}
      />
      {/* El resultado se queda a la vista aunque la barra ya se haya cerrado. */}
      {result && !busy && (
        <div className="bulk-result" onClick={() => setResult(null)} role="status">{result}</div>
      )}
    </>
  );
}
